import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { PayBill } from '../../../types/bookkeeping.js';

const STATUS_CLASSES: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  void: 'bg-slate-100 text-slate-500',
};

export function PayBills() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['bk-pay-bills', activeOrgId],
    queryFn: () => api<{ pay_bills: PayBill[] }>('/bookkeeping/purchases/pay-bills?limit=50'),
    enabled: !!activeOrgId,
  });

  const payBills = data?.pay_bills ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('purchases.pay_bills.title')}</h1>
        <Link
          to="/app/bookkeeping/purchases/pay-bills/new"
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('purchases.pay_bills.new')}
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('purchases.pay_bills.fields.number')}</th>
                <th className="px-3 py-2">{t('purchases.pay_bills.fields.vendor')}</th>
                <th className="px-3 py-2">{t('purchases.pay_bills.fields.payment_date')}</th>
                <th className="px-3 py-2">{t('purchases.pay_bills.fields.currency')}</th>
                <th className="px-3 py-2 text-right">{t('purchases.pay_bills.fields.gross')}</th>
                <th className="px-3 py-2 text-right">
                  {t('purchases.pay_bills.fields.withholding_total')}
                </th>
                <th className="px-3 py-2 text-right">{t('purchases.pay_bills.fields.net')}</th>
                <th className="px-3 py-2">{t('purchases.pay_bills.fields.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {payBills.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-4 text-center text-slate-400">
                    {t('purchases.pay_bills.empty', {
                      defaultValue: 'No hay pagos a proveedores aún.',
                    })}
                  </td>
                </tr>
              )}
              {payBills.map((pb) => (
                <tr key={pb.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/app/bookkeeping/purchases/pay-bills/${pb.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {pb.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{pb.vendor_id}</td>
                  <td className="px-3 py-2">{pb.payment_date}</td>
                  <td className="px-3 py-2">{pb.currency}</td>
                  <td className="px-3 py-2 text-right font-mono">{pb.gross_amount}</td>
                  <td className="px-3 py-2 text-right font-mono">{pb.withholding_total}</td>
                  <td className="px-3 py-2 text-right font-mono">{pb.net_amount}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-sm px-1.5 py-0.5 text-xs ${STATUS_CLASSES[pb.status] ?? ''}`}
                    >
                      {t(`purchases.pay_bills.statuses.${pb.status}`, { defaultValue: pb.status })}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/app/bookkeeping/purchases/pay-bills/${pb.id}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {t('journalEntries.viewEdit')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
